import { Cancel, ChevronRight, MonitorHeart, ReportProblem } from "@mui/icons-material";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../components/contextProviders/AlertProvider";
import EpiInfoBox from "../../../components/epi/EpiInfoBox";
import { EpiStatusChip } from "../../../components/epi/EpiStatusChip";
import EpiWizardActions from "../../../components/epi/EpiWizardActions";
import { EPI_COLORS } from "../../../components/epi/epiColors";
import { EpiInspectionFlowService } from "../../../services/EpiInspectionFlowService";
import { EpiPanelDetailDTO } from "../../../types/EpiPanelDetailDTO";
import { parseBrDate } from "../../epi-inspection-detail/EpiInspectionDetail";
import { StepProps } from "../EpiInspectionWizard";
import { DECLARATION_VERSION } from "./EpiSummaryStep";

const EXAM_DECLARATION =
	'Declaro que fui informado sobre as pendências de exames acima e entendi a importância '
	+ 'de realizá-los dentro do prazo estabelecido.';

const EpiExamsStep: FC<StepProps> = ({ inspection, onSave }) => {
	const [exams, setExams] = useState<EpiPanelDetailDTO[]>([]);
	const [accepted, setAccepted] = useState(false);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();
	const { showError } = useAlert();
	const service = new EpiInspectionFlowService();

	useEffect(() => {
		const load = async () => {
			const response = await service.listPendingExams(inspection.id);
			setExams(response?.data || []);
			setLoading(false);
		};
		load();
	}, []);

	return (
		<div className="passo-exames">
			<div className="painel cabecalho-exames">
				<span className="icone-atencao" style={{ backgroundColor: EPI_COLORS.warning }}>
					<ReportProblem fontSize="large" />
				</span>
				<div>
					<Typography variant="subtitle1">Pendências de Exames Ocupacionais</Typography>
					<span>Foram identificadas pendências de exames obrigatórios conforme o PCMSO.</span>
					<span>Informe ao colaborador e registre sua ciência abaixo.</span>
				</div>
			</div>
			<div className="painel">
				<Typography variant="subtitle1">Exames pendentes</Typography>
				{loading
					? <p>Carregando pendências…</p>
					: !exams.length
						? <p>Nenhuma pendência de exame encontrada.</p>
						: <div className="tabela tabela-exames">
							<div className="cabecalho-tabela">
								<span>EXAME</span>
								<span>TIPO</span>
								<span>SITUAÇÃO</span>
								<span>VENCIMENTO / PRAZO</span>
								<span>DIAS EM ATRASO</span>
							</div>
							{exams.map((exam, index) => (
								<div className="linha" key={index}>
									<div className="epi-nome">
										<span className="icone-epi" style={{ backgroundColor: EPI_COLORS.purple }}>
											<MonitorHeart fontSize="small" />
										</span>
										<div className="dados-exame">
											<span>{exam.detail || '—'}</span>
											{!!exam.periodicity && (
												<span className="periodicidade">
													Periodicidade: {exam.periodicity} meses
												</span>
											)}
										</div>
									</div>
									<span data-label="TIPO">
										{exam.examType
											? <EpiStatusChip label={exam.examType} color={EPI_COLORS.info} />
											: '—'}
									</span>
									<span data-label="SITUAÇÃO">
										{exam.status
											? <EpiStatusChip
												label={(exam.status || '').toUpperCase()}
												color={examStatusColor(exam.status)}
											/>
											: '—'}
									</span>
									<span data-label="VENCIMENTO / PRAZO">{exam.dueDate || '—'}</span>
									<span data-label="DIAS EM ATRASO" style={{ color: EPI_COLORS.danger }}>
										{overdueLabel(exam.dueDate)}
									</span>
								</div>
							))}
						</div>
				}
			</div>
			{!loading && !!exams.length && (
				<EpiInfoBox variant="warning" title="Atenção">
					<p>
						A realização dos exames ocupacionais é obrigatória e fundamental para a
						preservação da saúde do trabalhador.
					</p>
					<p>Em caso de dúvidas, procure o SESMT.</p>
				</EpiInfoBox>
			)}
			<div className="faixa-final">
				{!loading && !!exams.length && (
					<div className="painel">
						<Typography variant="subtitle1">Ciência do colaborador</Typography>
						<span className="declaracao-exame">{EXAM_DECLARATION}</span>
						<FormControlLabel
							label="Estou ciente das pendências de exames apresentadas."
							control={
								<Checkbox
									checked={accepted}
									onChange={(e) => setAccepted(e.target.checked)}
								/>
							}
						/>
						{!accepted && (
							<span style={{ color: EPI_COLORS.danger, fontSize: '13px' }}>
								É obrigatório marcar ciência para continuar.
							</span>
						)}
					</div>
				)}
				<div className="painel">
					<Typography variant="subtitle1">Informações adicionais</Typography>
					<EpiInfoBox variant="info">
						<p>
							Após registrar a ciência, o colaborador será direcionado para a
							coleta de assinatura.
						</p>
					</EpiInfoBox>
				</div>
			</div>
			<EpiWizardActions
				leftLabel="CANCELAR"
				leftIcon={<Cancel />}
				onLeft={() => navigate('/inspecao-epis/nova')}
				draftLabel="VOLTAR"
				onDraft={() => onSave(1, inspection.items)}
				primaryLabel={exams.length ? 'REGISTRAR CIÊNCIA E CONTINUAR' : 'CONTINUAR'}
				primaryIcon={<ChevronRight />}
				primaryDisabled={loading || (!!exams.length && !accepted)}
				onPrimary={confirm}
			/>
		</div>
	);

	function examStatusColor(status: string) {
		const upper = (status || '').toUpperCase();
		if (upper.includes('VENCIDO')) {
			return EPI_COLORS.danger;
		}
		if (upper.includes('NÃO APLICÁVEL') || upper.includes('NAO APLICAVEL')) {
			return EPI_COLORS.neutral;
		}
		return EPI_COLORS.warning;
	}

	function overdueLabel(dueDate?: string) {
		const time = parseBrDate(dueDate || '');
		if (time === null || Number.isNaN(time)) {
			return '—';
		}
		const today = new Date();
		today.setHours(0, 0, 0, 0);
		const days = Math.floor((today.getTime() - time) / 86400000);
		if (days <= 0) {
			return '—';
		}
		return days === 1 ? '1 dia' : `${days} dias`;
	}

	async function confirm() {
		try {
			if (exams.length) {
				await service.acknowledgeExams(inspection.id, EXAM_DECLARATION, DECLARATION_VERSION,
					exams.map(exam => `${exam.detail} | ${exam.status} | ${exam.dueDate}`).join('\n'));
			}
			await onSave(3, inspection.items);
		} catch (error: any) {
			showError(error?.response?.data || 'Não foi possível registrar a ciência dos exames.');
		}
	}
};

export default EpiExamsStep;
